import { Button, InputRef, Typography } from 'antd';
import { observer } from 'mobx-react-lite';
import * as React from 'react';
import { useTranslation } from 'react-i18next';

import { CustomDrawer, Formula, Spacing } from 'components/ui';
import { useLocalStore } from 'hooks/';
import { useEditFormStore } from 'stores/local';
import { FormulaSettingsStore } from 'stores/models/fieldsSettings';

import s from './FormulaSettingsDrawer.module.scss';

type FormulaDrawerProps = {
  setHeight: (height: number) => void;
};

const FormulaSettingsDrawer: React.FC<FormulaDrawerProps> = ({ setHeight }) => {
  const { t } = useTranslation();
  const { editFormStore, editFormStoreMediator } = useEditFormStore();

  const ref = React.useRef<InputRef | null>(null);

  const store = useLocalStore(
    () =>
      new FormulaSettingsStore({
        mediator: editFormStoreMediator,
        getTableRowEntityByCatalogId:
          editFormStore.dataModel.getTableRowEntityByCatalogId,
      })
  );

  const save = React.useCallback(() => {
    store.handleSave();
  }, [store]);

  const titleText = React.useMemo(() => {
    const fieldName = store.currentEntity?.formField.fieldData.name;
    return fieldName
      ? t('editForm.addFormulaForField', { fieldName })
      : t('editForm.addFormulaDefault');
  }, [store.currentEntity?.formField.fieldData.name, t]);

  React.useEffect(() => {
    if (store.drawerController.opened) {
      ref.current?.focus();
    }
  }, [store.drawerController.opened]);

  return (
    <CustomDrawer
      opened={store.drawerController.opened}
      onClose={store.drawerController.close}
      onChangeContentHeight={setHeight}
      mask={false}
      bodyScroll
      title={
        <Typography.Title className={s.drawer__title} level={3}>
          {titleText}
        </Typography.Title>
      }
      extra={
        <Spacing.Row>
          <Button onClick={store.drawerController.close}>
            {t('common.cancel')}
          </Button>
          <Button onClick={save} type="primary">
            {t('common.save')}
          </Button>
        </Spacing.Row>
      }
    >
      <CustomDrawer.Body
        onClick={store.newFormulaModel.focusToEnd}
        className={s.drawer__body}
      >
        <Formula formulaModel={store.newFormulaModel} />
      </CustomDrawer.Body>
    </CustomDrawer>
  );
};

export default observer(FormulaSettingsDrawer);
